import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Button,
  Flex,
  Text,
} from '@chakra-ui/react';
import { every, range } from 'lodash-es';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CREDIT_CARD from '../../../../../../shop/constants/credit-card';
import slPaymentService from '../../../../../../service/slPaymentService';
import { SECTION_STATUS } from '../../../../constants';
import FormField from '../FormField';
import ErrorPanel from '../ErrorPanel';
import AddCreditCardModal from './AddCreditCardModal';
import DeleteCreditCardModal from './DeleteCreditCardModal';
import { useAppContext } from '../../../../context/appContext';
import Loading from '../Loading';
import {
  CREDIT_CARD_STATUS,
  LOCAL_STORAGE_WEBHOOK_ID,
  QUERY_POLLING_ID,
} from './constants';
import useAsyncBindingCardStatus from './useAsyncBindingCardStatus';

const DEFAULT_CREDIT_CARD_IMG =
  'https://shoplineimg.com/assets/footer/card_default.png';

const MODAL_TYPE = {
  ADD: 'add',
  DELETE: 'delete',
};

const CreditCardField = () => {
  const { t } = useTranslation();
  const { slPaymentBindCard } = useAppContext();
  const [status, setStatus] = useState(SECTION_STATUS.INITIAL);
  const [creditCardList, setCreditCardList] = useState([]);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
  });

  const getCreditCardList = useCallback(async () => {
    try {
      setStatus(SECTION_STATUS.LOADING);
      const result = await slPaymentService.getPaymentInstruments();
      setCreditCardList(result.data);
      setStatus(SECTION_STATUS.SUCCESS);
    } catch (e) {
      console.error(e);
      setStatus(SECTION_STATUS.FAILURE);
    }
  }, []);
  const { startPolling, webhookMapRef } = useAsyncBindingCardStatus(
    slPaymentBindCard.pollingEndpoint,
    getCreditCardList,
  );

  const handleModalCancel = useCallback(() => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleAddCreditCardModalOpen = useCallback(() => {
    setModalConfig({
      isOpen: true,
      type: MODAL_TYPE.ADD,
    });
  }, []);

  const handleDeleteCreditCardModalOpen = useCallback((item) => {
    setModalConfig({
      isOpen: true,
      type: MODAL_TYPE.DELETE,
      item,
    });
  }, []);

  const handleDeleteCreditCardModalComplete = useCallback(() => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
    getCreditCardList();
  }, [getCreditCardList]);

  const checkPolling = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlPollingId = urlParams.get(QUERY_POLLING_ID);

    if (!urlPollingId) {
      const res = every(
        Object.values(webhookMapRef.current),
        (value) => value.isGetRes,
      );
      if (res) localStorage.removeItem(LOCAL_STORAGE_WEBHOOK_ID);
      return;
    }

    if (!webhookMapRef.current[urlPollingId]) {
      webhookMapRef.current = {
        ...webhookMapRef.current,
        [urlPollingId]: { isGetRes: false },
      };
    }
    startPolling();
  }, [startPolling, webhookMapRef]);

  useEffect(() => {
    getCreditCardList();
    checkPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === SECTION_STATUS.FAILURE) {
    return <ErrorPanel hideErrorImg onReload={getCreditCardList} />;
  }

  return (
    <FormField
      label={t('user.credit_card.title')}
      className="credit-card-field"
    >
      {creditCardList.length < CREDIT_CARD.CARD_LIST_LIMIT ? (
        <Button
          my="12px"
          mr="auto"
          variant="textButton"
          className="member-center-profile__text-button"
          data-e2e-id="member-center-profile-add-credit_button"
          onClick={handleAddCreditCardModalOpen}
        >
          {t('user.add_credit_card')}
        </Button>
      ) : (
        <Text
          mt="12px"
          mr="auto"
          variant="avatarText"
          textAlign="left"
          fontStyle="italic"
        >
          {t('user.add_credit_card.credit_card_limit', {
            max: CREDIT_CARD.CARD_LIST_LIMIT,
          })}
        </Text>
      )}
      {status === SECTION_STATUS.LOADING && <Loading />}
      {creditCardList.length > 0 && (
        <Accordion
          allowMultiple
          defaultIndex={range(CREDIT_CARD.CARD_LIST_LIMIT)}
        >
          <AccordionItem>
            <AccordionButton
              className="member-center-profile__according-button"
              padding="10px 0"
              mb="10px"
            >
              <AccordionIcon />
              <Text mr="auto" textAlign="left">
                {t('user.credit_card.information', {
                  max: CREDIT_CARD.CARD_LIST_LIMIT,
                })}
              </Text>
            </AccordionButton>
            <AccordionPanel className="credit-card-field__list">
              {creditCardList.map((item) => {
                const {
                  id,
                  status,
                  card: { expired, last, brand },
                } = item;
                const brandImg =
                  CREDIT_CARD.BRAND_ICON_MAP[brand] || DEFAULT_CREDIT_CARD_IMG;
                return (
                  <Flex
                    key={item.id}
                    className="credit-card-field__list-item"
                    alignItems="center"
                  >
                    <img width="40px" src={brandImg} />
                    <Box flex="1" paddingLeft="10px" textAlign="left">
                      <Text
                        variant={expired ? 'avatarDisableText' : 'avatarText'}
                        textAlign="left"
                      >
                        {`****${last}`}
                      </Text>
                      {expired && (
                        <Text variant="expiredTag" paddingLeft="10px">
                          {t('user.credit_card.expired')}
                        </Text>
                      )}
                    </Box>
                    {status === CREDIT_CARD_STATUS.SUCCESSED ? (
                      <Button
                        variant="textButton"
                        className="member-center-profile__text-button"
                        onClick={() =>
                          handleDeleteCreditCardModalOpen({
                            id,
                            expired,
                            last,
                            brandImg,
                          })
                        }
                      >
                        {t('user.credit_card_list.delete')}
                      </Button>
                    ) : (
                      <Text variant="creditLabel">
                        {t('user.credit_card_list.processing')}
                      </Text>
                    )}
                  </Flex>
                );
              })}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
      <AddCreditCardModal
        isOpen={modalConfig.type === MODAL_TYPE.ADD && modalConfig.isOpen}
        onCancel={handleModalCancel}
        onComplete={handleModalCancel}
      />
      <DeleteCreditCardModal
        isOpen={modalConfig.type === MODAL_TYPE.DELETE && modalConfig.isOpen}
        item={modalConfig.item}
        onCancel={handleModalCancel}
        onComplete={handleDeleteCreditCardModalComplete}
      />
    </FormField>
  );
};

export default CreditCardField;
